import { configureStore } from '@reduxjs/toolkit';
import userReducer from './slices/userSlice';
import tvReducer from './slices/tvSlice';
import menuReducer from './slices/menuSlice';
import mediaReducer from './slices/mediaSlice';
import authReducer, { setTakeawayList } from './slices/authSlice';
// Canvas Editor Reducers
import canvasReducer from '../canvas/redux/slices/canvasSlice';
import assetsReducer from '../canvas/redux/slices/assetsSlice';

// Middleware to persist auth state to localStorage
const authPersistMiddleware = (store) => (next) => (action) => {
  // Get current auth state before the action
  const prevAuthState = store.getState().auth;
  
  // Dispatch the action
  const result = next(action);
  
  // Only save to localStorage if this is an auth action
  // This prevents auth state from being affected by other slice actions
  if (action.type?.startsWith('auth/')) {
    const state = store.getState();
    const authState = state.auth;
    
    try {
      const stateToSave = {
        user: authState.user,
        registerNumber: authState.registerNumber,
        takeawayList: authState.takeawayList,
        isAuthenticated: authState.isAuthenticated,
      };
      localStorage.setItem('authState', JSON.stringify(stateToSave));
    } catch (err) {
      console.error('Error saving auth state to localStorage:', err);
    }
  }
  
  // IMPORTANT: If auth state was reset by a non-auth action, restore it from localStorage
  // This prevents auth state from being lost when other slices change
  if (!action.type?.startsWith('auth/')) {
    const currentAuthState = store.getState().auth;
    const hasTakeawayList = prevAuthState.takeawayList && prevAuthState.takeawayList.length > 0;
    const currentHasTakeawayList = currentAuthState.takeawayList && currentAuthState.takeawayList.length > 0;
    
    // If takeawayList was lost, restore from localStorage
    if (hasTakeawayList && !currentHasTakeawayList) {
      try {
        const savedState = localStorage.getItem('authState');
        if (savedState) {
          const parsed = JSON.parse(savedState);
          if (parsed.takeawayList && parsed.takeawayList.length > 0) {
            // Restore auth state by dispatching setTakeawayList action
            store.dispatch(setTakeawayList(parsed.takeawayList));
          }
        }
      } catch (err) {
        console.error('Error restoring auth state from localStorage:', err);
      }
    }
  }
  
  return result;
};

export const store = configureStore({
  reducer: {
    auth: authReducer,
    user: userReducer,
    tv: tvReducer,
    menu: menuReducer,
    media: mediaReducer,
    // Canvas Editor
    canvas: canvasReducer,
    assets: assetsReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false, // Needed for Firebase timestamps
    }).concat(authPersistMiddleware),
});

export default store;

